// src/domains/settings.ts —— 06 C-14 系统参数配置原生实现：GET /api/admin/settings → items；保存 PUT {items: 仅变更键}（后端部分更新）；无变更不发请求。
// 会话边界：session:lost / session:ready 各复位一次 st 切片并换代，旧会话在途的读取 / 保存结果丢弃（跨账号不串数据）
import { ApiError } from '../../../shared/src/api/client';
import { interpolate } from '../../../shared/src/errors';
import { parseDecimalInput, toMicro } from '../../../shared/src/money';
import { I18N_SETTINGS, REWRITES_SETTINGS } from '../i18n/settings';
import { can, gate, gatedStyle, type Gate } from '../perm';
import { settingsDefaults, type AlertTestChannel, type AlertTestState, type SettingsSlice } from '../state';
import { KEYS } from './settings.keys';
import { str, type AdminCtx, type AdminDomain, type InputEvent } from './types';

export type SettingKind = 'num' | 'switch' | 'text' | 'ro';

export interface SettingDef {
  key: string; // system_configs 键（camelCase，S-59）
  label: string; // t 键
  kind: SettingKind;
  suffix?: string; // 'USD' | '%' | 'USDT' | 'unitLevel'（t 键，其余原样显示）
  wide?: boolean; // text 宽 200（timezone / 客服 / 告警类，06 C-14）
}

// 06 C-14 ①～⑦、⑨ 安全、⑩ 卡片 分组与键序（⑤ 维护公告由独立卡渲染）
// 用户 2026-10-18：无作用的遗留键不再展示——rechargeRate / rechargeFixed / refundRate / refundFixed（卡费按卡产品配置，D33）、
// kycRetentionYears（无读取方）、sharedDepositMinRemainingPct（无读取方）、depositChannelFee*（恒 0 只读）。后端键与值保留不读。
// 卡类费用键（openFee / closeFee / txFee* / crossBorder* / declinedFee / disputeFee）只作「新同步卡产品」的默认值，标签带「新产品默认」。
export const GROUPS: ReadonlyArray<{ title: string; fields: readonly SettingDef[] }> = [
  { title: 'gFee', fields: [
    { key: 'openFee', label: 'cfgOpenFee', kind: 'num', suffix: 'USD' },
    { key: 'depositFeeRate', label: 'cfgDepositFeeRate', kind: 'num', suffix: '%' },
    { key: 'fxBaseRate', label: 'cfgFXBaseRate', kind: 'num', suffix: 'USDT' },
    { key: 'fxSpreadLimitPct', label: 'cfgFXSpreadLimit', kind: 'num', suffix: '%' },
    { key: 'upstreamRechargeCostRate', label: 'cfgUpCostRate', kind: 'num', suffix: '%' },
    { key: 'closeFee', label: 'cfgCloseFee', kind: 'num', suffix: 'USD' },
    { key: 'withdrawFeeRate', label: 'cfgWithdrawFeeRate', kind: 'num', suffix: '%' },
    { key: 'cardWithdrawFee', label: 'cfgCardWithdrawFee', kind: 'num', suffix: 'USDT' },
    { key: 'txFeeRate', label: 'cfgTxFeeRate', kind: 'num', suffix: '%' },
    { key: 'txFeeFixed', label: 'cfgTxFeeFixed', kind: 'num', suffix: 'USD' },
    { key: 'crossBorderRate', label: 'cfgCrossRate', kind: 'num', suffix: '%' },
    { key: 'crossBorderFixed', label: 'cfgCrossFixed', kind: 'num', suffix: 'USD' },
    { key: 'declinedFee', label: 'cfgDeclinedFee', kind: 'num', suffix: 'USD' },
    { key: 'disputeFee', label: 'cfgDisputeFee', kind: 'num', suffix: 'USD' },
    { key: 'upstreamBudgetTopupFeeFixed', label: 'cfgUpTopupFeeFixed', kind: 'num', suffix: 'USD' },
    { key: 'upstreamBudgetTopupFeeRate', label: 'cfgUpTopupFeeRate', kind: 'num', suffix: '%' },
    { key: 'upstreamBudgetWithdrawFeeFixed', label: 'cfgUpWithdrawFeeFixed', kind: 'num', suffix: 'USD' },
    { key: 'upstreamBudgetWithdrawFeeRate', label: 'cfgUpWithdrawFeeRate', kind: 'num', suffix: '%' },
  ] },
  { title: 'gLimit', fields: [
    { key: 'minDeposit', label: 'cfgMinDeposit', kind: 'num' },
    { key: 'minWithdraw', label: 'cfgMinWithdraw', kind: 'num' },
    { key: 'feeTrc', label: 'cfgFeeTrc', kind: 'num' },
    // feeErc / feeBep / withdrawBepEnabled：2026-10-15 旧功能下线——提币只开 TRC20（后端 1503 withdraw_trc20_only），
    // 三键不展示、不提交（后端键与值保留，见 docs/reviews/2026-10-15-legacy-feature-cleanup.md）。
    { key: 'feeTrcActivation', label: 'cfgFeeTrcActivation', kind: 'num', suffix: 'USDT' },
    { key: 'kycLimit', label: 'cfgKycLimit', kind: 'num' },
    { key: 'kycLimitVerified', label: 'cfgKycLimitVerified', kind: 'num' },
    { key: 'maxCards', label: 'cfgMaxCards', kind: 'num' },
    { key: 'maxOpenPerDay', label: 'cfgMaxOpenPerDay', kind: 'num' }, // 0050：单用户每日开卡上限（非负整数，0 = 不限）
    { key: 'depositReviewThreshold', label: 'cfgDepositReview', kind: 'num' },
    { key: 'withdrawReviewThreshold', label: 'cfgWdReview', kind: 'num' },
    { key: 'payoutAutoThreshold', label: 'cfgPayoutAuto', kind: 'num' }, // 0038：Console 自动付款额度（受财务机硬上限约束）
    { key: 'topupMatchTolerance', label: 'cfgTolerance', kind: 'num' },
    { key: 'reserveAlertThreshold', label: 'cfgReserveAlert', kind: 'num' },
  ] },
  { title: 'gSwitch', fields: [
    { key: 'openCard', label: 'cfgOpenCard', kind: 'switch' },
    { key: 'deposit', label: 'cfgDeposit', kind: 'switch' },
    { key: 'withdraw', label: 'cfgWithdraw', kind: 'switch' },
    { key: 'register', label: 'cfgRegister', kind: 'switch' },
    { key: 'maint', label: 'cfgMaint', kind: 'switch' },
  ] },
  { title: 'gComm', fields: [
    { key: 'l1Open', label: 'cfgL1Open', kind: 'num' },
    { key: 'l1Top', label: 'cfgL1Top', kind: 'num', suffix: '%' },
    { key: 'l2Open', label: 'cfgL2Open', kind: 'num' },
    { key: 'l2Top', label: 'cfgL2Top', kind: 'num', suffix: '%' },
    { key: 'maxLevel', label: 'cfgMaxLevel', kind: 'num', suffix: 'unitLevel' }, // 用户 2026-10-18：多级代理，整数 2～100（默认 10）
    { key: 'minCommWd', label: 'cfgMinCommWd', kind: 'num' },
    { key: 'maxAgentSpendRate', label: 'cfgMaxAgentRate', kind: 'num', suffix: '%' },
    { key: 'agentWdFeeUsdt', label: 'cfgWdFeeUsdt', kind: 'num', suffix: 'USD' },
    { key: 'agentWdFeeWallet', label: 'cfgWdFeeWallet', kind: 'num', suffix: 'USD' },
  ] },
  { title: 'gPlatform', fields: [
    { key: 'timezone', label: 'cfgTimezone', kind: 'text', wide: true },
    { key: 'supportTelegram', label: 'cfgSupportTg', kind: 'text', wide: true },
    { key: 'supportEmail', label: 'cfgSupportEmail', kind: 'text', wide: true },
    { key: 'supportHours', label: 'cfgSupportHours', kind: 'text', wide: true },
    { key: 'siteUrl', label: 'cfgSiteUrl', kind: 'text', wide: true },
    { key: 'appUrl', label: 'cfgAppUrl', kind: 'text', wide: true },
  ] },
  { title: 'gOps', fields: [
    { key: 'alertChatId', label: 'cfgAlertChatId', kind: 'text', wide: true },
    { key: 'alertEmail', label: 'cfgAlertEmail', kind: 'text', wide: true },
    { key: 'withdrawalPaySlaHours', label: 'cfgPaySla', kind: 'num' },
  ] },
 {title:'gSharedPayments',fields:[
 {key:'sharedDepositEnabledTrc',label:'cfgSharedTrc',kind:'switch'},
 {key:'sharedDepositEnabledErc',label:'cfgSharedErc',kind:'switch'},
 {key:'sharedDepositEnabledBep',label:'cfgSharedBep',kind:'switch'},
 {key:'sharedDepositOrderTtlSeconds',label:'cfgSharedTtl',kind:'num',suffix:'s'},
 {key:'sharedDepositMaxPendingPerUser',label:'cfgSharedPending',kind:'num'},
 {key:'sharedDepositMaxNewOrdersPerDay',label:'cfgSharedDaily',kind:'num'},
 ]},
  // 2026-10-15 用户拍板：Console / 代理端空闲超时与最长在线、用户端 refresh 天数（服务端强制，06 C-14 ⑨）
  { title: 'gSecurity', fields: [
    { key: 'adminIdleMinutes', label: 'cfgAdminIdle', kind: 'num' },
    { key: 'adminMaxHours', label: 'cfgAdminMaxHours', kind: 'num' },
    { key: 'agentIdleMinutes', label: 'cfgAgentIdle', kind: 'num' },
    { key: 'agentMaxHours', label: 'cfgAgentMaxHours', kind: 'num' },
    { key: 'userRefreshDays', label: 'cfgUserRefreshDays', kind: 'num' },
  ] },
  // 06 C-14 ⑩：卡已消费回写冷静期（后端 1–1440，本页限 1–60）
  { title: 'gCard', fields: [
    { key: 'cardSpentCoolingMinutes', label: 'cfgCardSpentCooling', kind: 'num' },
    // 0052：预算剩余开卡名额告警阈值（整数 0–10000，0 = 只关低水位；名额用完的严重告警不受影响）
    { key: 'budgetCardLowThreshold', label: 'cfgBudgetCardLow', kind: 'num' },
  ] },
  // 0041 运维健康巡检（worker ops.health）：阈值与站点列表；告警经「告警与合规」两键投递
  { title: 'gHealth', fields: [
    { key: 'opsHealthEnabled', label: 'cfgOpsEnabled', kind: 'switch' },
    { key: 'opsHealthIntervalSeconds', label: 'cfgOpsInterval', kind: 'num', suffix: 's' },
    { key: 'opsDiskWarnPct', label: 'cfgOpsDiskWarn', kind: 'num', suffix: '%' },
    { key: 'opsDiskCritPct', label: 'cfgOpsDiskCrit', kind: 'num', suffix: '%' },
    { key: 'opsMemWarnPct', label: 'cfgOpsMemWarn', kind: 'num', suffix: '%' },
    { key: 'opsMemCritPct', label: 'cfgOpsMemCrit', kind: 'num', suffix: '%' },
    { key: 'opsLoadWarn', label: 'cfgOpsLoadWarn', kind: 'num' },
    { key: 'opsLoadCrit', label: 'cfgOpsLoadCrit', kind: 'num' },
    { key: 'opsSiteUrls', label: 'cfgOpsSiteUrls', kind: 'text', wide: true },
    { key: 'opsSiteFailRounds', label: 'cfgOpsSiteFail', kind: 'num' },
    { key: 'opsSiteP95WarnMs', label: 'cfgOpsSiteP95', kind: 'num', suffix: 'ms' },
    { key: 'opsTlsWarnDays', label: 'cfgOpsTlsWarn', kind: 'num' },
    { key: 'opsTlsCritDays', label: 'cfgOpsTlsCrit', kind: 'num' },
    { key: 'opsDbPingWarnMs', label: 'cfgOpsDbPing', kind: 'num', suffix: 'ms' },
    { key: 'opsDbConnWarnPct', label: 'cfgOpsDbConn', kind: 'num', suffix: '%' },
    { key: 'opsTreasuryStaleMinutes', label: 'cfgOpsTreasuryStale', kind: 'num' },
    { key: 'opsBackupMaxAgeHours', label: 'cfgOpsBackupAge', kind: 'num' },
  ] },
];

// 运营 UX 评审 2026-10-15 #5：分组说明（开关的前置条件等）；按分组 title 挂接，不改 GROUPS 结构
// gFee：平台补款成本率同时用于利润台账按充值计提上游补款成本（0048，06 C-14 / C-32）
// gLimit：自动付款额度 + 每日开卡上限（0050）两条说明，按序拼接；gCard：预算开卡名额告警（0052）
const GROUP_HINTS: Readonly<Record<string, readonly string[]>> = {
  gFee: ['cfgFeeHint'], gSharedPayments: ['cfgSharedHint'], gSecurity: ['cfgSecurityHint'], gLimit: ['cfgPayoutAutoHint', 'cfgMaxOpenPerDayHint'], gHealth: ['cfgOpsHint'],
  gCard: ['cfgBudgetCardLowHint'],
};
const GROUP_HINT_STYLE = 'font-size:12px;line-height:1.6;color:#6b7772;background:#f8faf9;border-radius:10px;padding:8px 12px;margin-top:-4px';

// 公告卡（原型 L191–196）单独渲染的两键
const NOTICE: readonly SettingDef[] = [
  { key: 'settleTime', label: 'cfgSettleTime', kind: 'text' },
  { key: 'maintText', label: 'cfgMaintText', kind: 'text' },
];

// 本页可编辑键全集：后端 items 中其余键（confirmTrc 等）不展示也不提交；'ro' 字段只展示不提交（2026-10-18 起 maxLevel 可编辑）
const EDITABLE: ReadonlyMap<string, SettingDef> = new Map([...GROUPS.flatMap((g) => g.fields), ...NOTICE].filter((d) => d.kind !== 'ro').map((d) => [d.key, d] as const));

const SETTLE_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CHAT_ID_RE = /^(-?\d{1,20}|@[A-Za-z0-9_]{5,32})$/;
const CONTROL_RE = /[\u0000-\u001f\u007f-\u009f]/;
const TEXT_CONTROL_RE = /[\u0000-\u0009\u000b-\u000c\u000e-\u001f\u007f-\u009f]/;
const INT64_MAX = 9223372036854775807n;
const INT_KEYS: Readonly<Record<string, number>> = { maxCards: 0, maxOpenPerDay: 0, withdrawalPaySlaHours: 1 }; // 值 = 下限
const BELOW_100 = new Set(['depositFeeRate', 'withdrawFeeRate']); // 0 ≤ x < 100
// 整数闭区间键：[下限, 上限]（06 C-14 ⑨ / ⑩；cardSpentCoolingMinutes 后端 1–1440，本页按运营口径收紧到 1–60）
const INT_RANGES: Readonly<Record<string, readonly [number, number]>> = {
  adminIdleMinutes: [5, 1440],
  adminMaxHours: [1, 720],
  agentIdleMinutes: [5, 1440],
  agentMaxHours: [1, 720],
  userRefreshDays: [1, 90],
  cardSpentCoolingMinutes: [1, 60],
  maxLevel: [2, 100], // 代理最大层级（与后端 syscfg.MaxLevelMin / MaxLevelMax 同区间）
  budgetCardLowThreshold: [0, 10000], // 0052（与后端 syscfg.specs / budget.CardLowThresholdMax 同区间）
  // 0041 运维巡检（与后端 syscfg.specs / opshealth.IntKeys 同区间）
  opsHealthIntervalSeconds: [30, 3600],
  opsDiskWarnPct: [1, 100],
  opsDiskCritPct: [1, 100],
  opsMemWarnPct: [1, 100],
  opsMemCritPct: [1, 100],
  opsSiteFailRounds: [1, 60],
  opsSiteP95WarnMs: [100, 60000],
  opsTlsWarnDays: [1, 365],
  opsTlsCritDays: [1, 365],
  opsDbPingWarnMs: [10, 60000],
  opsDbConnWarnPct: [1, 100],
  opsTreasuryStaleMinutes: [1, 1440],
  opsBackupMaxAgeHours: [1, 720],
};
const POSITIVE_KEYS = new Set(['opsLoadWarn', 'opsLoadCrit']); // 0 < x（每核 5 分钟负载）

// 0041 站点列表：逗号 / 空白分隔；每项 https://host[/path]、无用户信息与片段、可打印 ASCII、≤ 256 字符；去重后 ≤ 10 项、
// 逗号连接 ≤ 1024 字符（system_configs.cfg_value）；空串 = 不巡检站点。返回规范形态（与后端 kURLList 一致），非法返回 null。
const SITE_URL_MAX = 10;
export function normalizeSiteUrls(raw: string): string | null {
  const out: string[] = [];
  for (const item of raw.split(/[ ,\t\r\n]+/).filter((x) => x !== '')) {
    if (item.length > 256 || !/^[\x21-\x7e]+$/.test(item) || item.includes('#') || !/^https:\/\/[^/]/i.test(item)) return null;
    try {
      const u = new URL(item);
      if (u.protocol !== 'https:' || u.hostname === '' || u.username !== '' || u.password !== '') return null;
    } catch { return null; }
    if (!out.includes(item)) out.push(item);
  }
  const joined = out.join(',');
  return out.length > SITE_URL_MAX || joined.length > 1024 ? null : joined;
}

// 原型 L176 保存按钮、L186 数值输入、L193 结算时间输入样式串逐字
const SAVE_STYLE = 'height:38px;padding:0 18px;border:0;border-radius:10px;background:#0d9a68;color:#fff;font-weight:700;font-size:13px;cursor:pointer;display:inline-flex;align-items:center;gap:8px';
const NUM_STYLE = 'width:110px;height:38px;border:1px solid #dfe5e2;border-radius:9px;padding:0 10px;font-size:14px;font-weight:700;text-align:right;outline:none;font-variant-numeric:tabular-nums';
const textStyle = (wide: boolean): string => `width:${wide ? 200 : 140}px;height:38px;border:1px solid #dfe5e2;border-radius:9px;padding:0 10px;font-size:13px;font-weight:700;text-align:right;outline:none`;
const INVALID = ';border-color:#d1434b';
// 运营 UX 评审 P0 #2：已改未保存的字段琥珀色描边 + 浅底（沿用页眉条 #c47a0c / #fff3df 色板）
const CHANGED = ';border-color:#e0a03a;background:#fff8ea';
const SWITCH_CHANGED = ';box-shadow:0 0 0 3px #f5c46b';
const SAVE_IDLE = ';opacity:.55'; // 无修改时保存按钮置灰（仍可点：点了提示「没有需要保存的修改」）
const DIRTY_BAR_STYLE = 'flex-basis:100%;font-size:12.5px;font-weight:700;line-height:1.6;color:#8a5a00;background:#fff3df;border:1px solid #f5c46b;border-radius:10px;padding:8px 12px';

// D-42（用户报障根因修复 2026-10-10）：出厂种子把 siteUrl / appUrl 建为空串，为空会让官网登录 / 注册与 Bot Mini App 入口不可用。
// 后端不强制必填（避免卡住初装流程），本页只做显著可见告警：琥珀色板沿用页眉条 cfgWarn / cfgDirty 的 #c47a0c + #fff3df。
const EMPTY_WARN_KEYS: ReadonlySet<string> = new Set(['siteUrl', 'appUrl']);
const WARN_STYLE = 'font-size:11.5px;font-weight:700;line-height:1.6;color:#c47a0c;background:#fff3df;border-radius:9px;padding:6px 10px';

// 单个设置值是否合法（仅校验本次变更的键；后端仍做强校验）
export function validSetting(key: string, raw: string): boolean {
  const v = raw.trim();
  const def = EDITABLE.get(key);
  if (!def) return false;
  if (key === 'settleTime') return SETTLE_RE.test(v);
  if (key === 'maintText') return Array.from(raw).length <= 1024 && !TEXT_CONTROL_RE.test(raw);
  if (key === 'supportTelegram' || key === 'supportHours') return Array.from(raw).length <= 64 && !CONTROL_RE.test(raw);
  if (key === 'alertChatId') return v === '' || CHAT_ID_RE.test(v);
  if (key === 'supportEmail' || key === 'alertEmail') return v === '' || EMAIL_RE.test(v);
  if (key === 'siteUrl' || key === 'appUrl') {
    if (v === '') return true;
    try {
      const u = new URL(v);
      return u.protocol === 'https:' && u.host !== '' && u.username === '' && u.password === '' &&
        u.pathname === '/' && u.search === '' && u.hash === '' && !v.endsWith('/') && /^[\x00-\x7f]+$/.test(v);
    } catch { return false; }
  }
  if (key === 'opsSiteUrls') return normalizeSiteUrls(raw) !== null;
  if (key === 'timezone') {
    if (v === '' || v === 'Local') return false;
    try { new Intl.DateTimeFormat('en-US', { timeZone: v }); return true; }
    catch { return false; }
  }
  if (key==='sharedDepositOrderTtlSeconds') return /^\d+$/.test(v)&&Number(v)>=300&&Number(v)<=600;
  if (key==='sharedDepositMaxPendingPerUser') return /^\d+$/.test(v)&&Number(v)>=1&&Number(v)<=100;
  if (key==='sharedDepositMaxNewOrdersPerDay') return /^\d+$/.test(v)&&Number(v)>=1&&Number(v)<=10000;
  if (def.kind === 'switch') return v === '1' || v === '0';
  const range = INT_RANGES[key];
  if (range) return /^\d{1,9}$/.test(v) && Number(v) >= range[0] && Number(v) <= range[1];
  const min = INT_KEYS[key];
  if (min !== undefined) return /^\d+$/.test(v) && BigInt(v) >= BigInt(min) && BigInt(v) <= INT64_MAX;
  const n = parseDecimalInput(v, 6);
  if (n === null) return false;
  // 用户拍板 2026-10-15：平台兑换汇率 = 1 USDT 入账多少 USDT 余额，0 < b ≤ 1、≤ 6 位小数（服务端同校验，越界 4220）
  if (key === 'fxBaseRate') return toMicro(n) > 0n && toMicro(n) <= toMicro('1');
  if (key === 'fxSpreadLimitPct') return toMicro(n) <= toMicro('50');
  if (POSITIVE_KEYS.has(key)) return toMicro(n) > 0n;
  // 0038：Console 自动付款额度 0～1000000 USDT、≤ 2 位小数（服务端同校验，越界 4220）
  if (key === 'payoutAutoThreshold') return parseDecimalInput(v, 2) !== null && toMicro(n) <= toMicro('1000000');
  if (def.suffix === '%') {
    const m = toMicro(n);
    return BELOW_100.has(key) ? m < toMicro('100') : m <= toMicro('100');
  }
  return true;
}

interface StRun {
  ready: boolean;
}

const runs = new WeakMap<object, StRun>();
const unsubs = new WeakMap<object, ReadonlyArray<() => void>>();

function runOf(ctx: AdminCtx): StRun {
  let r = runs.get(ctx.host);
  if (!r) {
    r = { ready: true }; // 未经 mount 的宿主（域单测）视为已就绪
    runs.set(ctx.host, r);
  }
  return r;
}

function guard(ctx: AdminCtx): () => boolean {
  const run = runOf(ctx);
  const host = ctx.host;
  return () => runs.get(host) === run;
}

function resetSession(ctx: AdminCtx, ready: boolean): void {
  runs.set(ctx.host, { ready });
  const st = ctx.host.state.st;
  if (st.loaded || st.saving || st.err || st.warn || st.dirty || st.leaveTo !== null || st.alertTest.busy || st.alertTest.result !== null || Object.keys(st.items).length > 0) {
    ctx.host.setState({ st: settingsDefaults() });
  }
}

function setSt(ctx: AdminCtx, patch: Partial<SettingsSlice>): void {
  ctx.host.setState({ st: { ...ctx.host.state.st, ...patch } });
}

function stringItems(v: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof v !== 'object' || v === null) return out;
  for (const [k, x] of Object.entries(v)) if (typeof x === 'string') out[k] = x;
  return out;
}

function changedKeys(items: Readonly<Record<string, string>>, base: Readonly<Record<string, string>>): string[] {
  return [...EDITABLE.keys()].filter((k) => k in items && items[k] !== base[k]);
}

function labelOfKey(ctx: AdminCtx, key: string): string {
  const def = EDITABLE.get(key);
  return def ? str(ctx.t, def.label) : key;
}

function fieldErr(ctx: AdminCtx, keys: readonly string[]): string {
  return keys.map((k) => interpolate(str(ctx.t, 'errCfgField'), { field: labelOfKey(ctx, k) })).join('；');
}

function canEdit(ctx: AdminCtx): boolean {
  return can(ctx.host.state.sh.me, 'settings', 'edit');
}

export async function loadSettings(ctx: AdminCtx): Promise<void> {
  const alive = guard(ctx);
  try {
    const r = await ctx.gp.api.call<{ items?: unknown }>('GET /api/admin/settings');
    if (!alive()) return;
    const items = stringItems(r.items);
    setSt(ctx, { ...settingsDefaults(), loaded: true, items, base: items });
  } catch (e) {
    if (!alive()) return;
    ctx.gp.ui.toast(ctx.gp.ui.err(e));
  }
}

function setItem(ctx: AdminCtx, key: string, value: string): void {
  if (!canEdit(ctx) || !EDITABLE.has(key)) return;
  const st = ctx.host.state.st;
  if (!st.loaded) return;
  const items = { ...st.items, [key]: value };
  const invalid = st.invalid.filter((k) => k !== key);
  setSt(ctx, { items, dirty: changedKeys(items, st.base).length > 0, invalid, err: invalid.length ? st.err : '' });
}

interface LossWarning {
  code: string;
  effective_rate: string;
  upstream_cost_rate: string;
}

// D2（用户 2026-10-18）：maxLevel 调低到小于当前最深在册层级 → 保存成功但提示（存量不受影响，仅阻止新建超限下级）
function maxLevelWarning(v: unknown): { max_level: number; deepest_level: number } | null {
  if (!Array.isArray(v)) return null;
  for (const w of v) {
    if (typeof w !== 'object' || w === null || !('code' in w) || w.code !== 'max_level_below_existing') continue;
    const m = 'max_level' in w ? w.max_level : undefined;
    const d = 'deepest_level' in w ? w.deepest_level : undefined;
    if (typeof m === 'number' && typeof d === 'number') return { max_level: m, deepest_level: d };
  }
  return null;
}

function lossWarning(v: unknown): LossWarning | null {
  if (!Array.isArray(v)) return null;
  for (const w of v) {
    if (typeof w !== 'object' || w === null) continue;
    if (!('code' in w) || w.code !== 'deposit_fee_loss') continue;
    const e = 'effective_rate' in w ? w.effective_rate : undefined;
    const u = 'upstream_cost_rate' in w ? w.upstream_cost_rate : undefined;
    return { code: 'deposit_fee_loss', effective_rate: typeof e === 'string' ? e : String(e ?? ''), upstream_cost_rate: typeof u === 'string' ? u : String(u ?? '') };
  }
  return null;
}

// 回读核对：数值键按 6 位小数定点比较（后端会规范化「1.50」→「1.5」等写法），其余键按 trim 后原文比较
function sameSetting(key: string, sent: string, got: string | undefined): boolean {
  if (got === undefined) return false;
  if (key === 'opsSiteUrls') return normalizeSiteUrls(sent) === got.trim(); // 后端规范化为逗号连接、去重保序
  if (EDITABLE.get(key)?.kind === 'num') {
    const a = parseDecimalInput(sent.trim(), 6);
    const b = parseDecimalInput(got.trim(), 6);
    if (a !== null && b !== null) return toMicro(a) === toMicro(b);
  }
  return sent.trim() === got.trim();
}

// 修改项标签串：最多列 6 项，其余「等 N 项」，避免吸顶条过长
function labelList(ctx: AdminCtx, keys: readonly string[]): string {
  const head = keys.slice(0, 6).map((k) => labelOfKey(ctx, k)).join('、');
  return keys.length > 6 ? `${head}${interpolate(str(ctx.t, 'cfgUsMore'), { n: keys.length - 6 })}` : head;
}

export async function saveSettings(ctx: AdminCtx): Promise<void> {
  const t = ctx.t;
  const st = ctx.host.state.st;
  if (!canEdit(ctx) || !st.loaded || st.saving) return;
  const keys = changedKeys(st.items, st.base);
  // 运营 UX 评审 P0 #1：无变更不发 PUT（后端空 items → 4220 field=items），且不得报「已保存」——明确提示没有需要保存的修改
  if (keys.length === 0) {
    setSt(ctx, { dirty: false });
    ctx.gp.ui.toast(str(t, 'cfgUsNothing'));
    return;
  }
  const invalid = keys.filter((k) => !validSetting(k, st.items[k] ?? ''));
  if (invalid.length) {
    setSt(ctx, { invalid, err: fieldErr(ctx, invalid), warn: '' });
    return;
  }
  const body: Record<string, string> = Object.fromEntries(keys.map((k) => [k, (st.items[k] ?? '').trim()] as const));
  const alive = guard(ctx);
  setSt(ctx, { saving: true, err: '', warn: '', invalid: [] });
  let r: { warnings?: unknown; fx_out_of_band?: { total: number } | null };
  try {
    r = await ctx.gp.api.call<{ warnings?: unknown; fx_out_of_band?: { total: number } | null }>('PUT /api/admin/settings', { body: { items: body } });
  } catch (e) {
    if (!alive()) return;
    if (e instanceof ApiError && e.code === 4220 && e.data.field && EDITABLE.has(e.data.field)) {
      setSt(ctx, { saving: false, invalid: [e.data.field], err: fieldErr(ctx, [e.data.field]) });
      return;
    }
    if (e instanceof ApiError && e.code === 1303) {
      // 06 C-14：toast err1303；0045 起只校验用户链奖励上限（data.field=l1Open 等），不再返回超限代理列表 → 标红对应参数
      const f = e.data.field && EDITABLE.has(e.data.field) ? [e.data.field] : [];
      setSt(ctx, { saving: false, invalid: f, err: str(t, 'err1303') });
      ctx.gp.ui.toast(str(t, 'err1303'));
      return;
    }
    setSt(ctx, { saving: false });
    ctx.gp.ui.toast(ctx.gp.ui.err(e));
    return;
  }
  if (!alive()) return;
  const loss = lossWarning(r.warnings);
  const lvl = maxLevelWarning(r.warnings);
  const warnOf = (items: Readonly<Record<string, string>>): string => [
    loss ? interpolate(str(t, 'cfgWarnLoss'), { effective_rate: loss.effective_rate, upstream_cost_rate: loss.upstream_cost_rate }) : '',
    lvl ? interpolate(str(t, 'cfgWarnMaxLevel'), { max: lvl.max_level, deepest: lvl.deepest_level }) : '',
    r.fx_out_of_band?.total ? interpolate(str(t, 'cfgFXOutOfBand'), { total: r.fx_out_of_band.total }) : '',
    items['maint'] === '1' && (items['maintText'] ?? '').trim() === '' ? str(t, 'maintTextEmpty') : '',
  ].filter((x) => x !== '').join('；');
  // 运营 UX 评审 P0 #4：PUT 成功后重新 GET，逐项核对已落库的值，不一致即报错（不能只凭 PUT 200 宣称成功）
  let fresh: Record<string, string>;
  try {
    const rb = await ctx.gp.api.call<{ items?: unknown }>('GET /api/admin/settings');
    fresh = stringItems(rb.items);
  } catch (e) {
    if (!alive()) return;
    // 读回失败：PUT 已成功，按提交值记为基线，但明确告知未核对
    const cur = ctx.host.state.st;
    const base = { ...cur.base, ...body };
    const items = { ...cur.items, ...Object.fromEntries(keys.filter((k) => cur.items[k] === st.items[k]).map((k) => [k, body[k] ?? ''] as const)) };
    const msg = interpolate(str(t, 'cfgUsReadbackFail'), { err: ctx.gp.ui.err(e) });
    setSt(ctx, { items, base, dirty: changedKeys(items, base).length > 0, saving: false, warn: warnOf(items), err: msg });
    ctx.gp.ui.toast(msg);
    return;
  }
  if (!alive()) return;
  const cur = ctx.host.state.st;
  const mismatch = keys.filter((k) => !sameSetting(k, body[k] ?? '', fresh[k]));
  // 新基线 = 库里读回的值；保存在途期间又改过的键保留编辑值；回读不一致的键保留提交值（继续显示为未保存，便于重试）
  const editedSince = [...EDITABLE.keys()].filter((k) => cur.items[k] !== st.items[k] && k in cur.items);
  const items: Record<string, string> = {
    ...fresh,
    ...Object.fromEntries(mismatch.map((k) => [k, body[k] ?? ''] as const)),
    ...Object.fromEntries(editedSince.map((k) => [k, cur.items[k] ?? ''] as const)),
  };
  const base = fresh;
  const err = mismatch.length ? interpolate(str(t, 'cfgUsMismatch'), { labels: labelList(ctx, mismatch) }) : '';
  setSt(ctx, { items, base, dirty: changedKeys(items, base).length > 0, saving: false, warn: warnOf(items), err });
  if (mismatch.length) {
    ctx.gp.ui.toast(err);
    return;
  }
  ctx.gp.ui.toast(interpolate(str(t, 'cfgUsSaved'), { n: keys.length, labels: labelList(ctx, keys) }));
}

// 确认离开：丢弃未保存修改并切到被拦截的目标页（与外壳 go() 同口径：复位页签 + 刷新 me）
// ── 发送测试告警（06 C-14 ⑦，2026-10-20）：POST /api/admin/alerts/test，按已保存的 alertChatId / alertEmail 各发一条；
// 服务端限速 1 次 / 30s / 管理员（429 → 通用 err1002 文案）。有未保存修改时不发送，提示先保存（测试的是库中配置，不是草稿）。
const ALERT_TEST_GROUP = 'gOps';
const ALERT_TEST_BTN = 'height:34px;padding:0 14px;border:1px solid #0d9a68;border-radius:9px;background:#fff;color:#0d9a68;font-weight:700;font-size:12.5px;cursor:pointer;display:inline-flex;align-items:center;gap:8px';
const ALERT_TEST_SPIN = 'width:12px;height:12px;border:2px solid rgba(13,154,104,.3);border-top-color:#0d9a68;border-radius:50%;display:inline-block;animation:gpSpin .7s linear infinite';
const ALERT_TEST_ROW = 'font-size:12.5px;font-weight:700;line-height:1.6;border-radius:9px;padding:6px 10px';
const ALERT_TEST_OK = `${ALERT_TEST_ROW};color:#0d7a53;background:#e8f6f0`;
const ALERT_TEST_FAIL = `${ALERT_TEST_ROW};color:#b3261e;background:#fdecec`;

function setAlertTest(ctx: AdminCtx, patch: Partial<AlertTestState>): void {
  setSt(ctx, { alertTest: { ...ctx.host.state.st.alertTest, ...patch } });
}

function parseChannel(v: unknown): AlertTestChannel {
  if (typeof v !== 'object' || v === null) return { ok: false, error: '' };
  const ok = Reflect.get(v, 'ok') === true;
  const err: unknown = Reflect.get(v, 'error');
  return { ok, error: ok || typeof err !== 'string' ? '' : err };
}

export async function sendAlertTest(ctx: AdminCtx): Promise<void> {
  const st = ctx.host.state.st;
  if (!canEdit(ctx) || !st.loaded || st.alertTest.busy) return;
  if (st.dirty) {
    ctx.gp.ui.toast(str(ctx.t, 'cfgAlertTestSaveFirst'));
    return;
  }
  const alive = guard(ctx);
  setAlertTest(ctx, { busy: true, result: null });
  let r: { telegram?: unknown; email?: unknown };
  try {
    r = await ctx.gp.api.call<{ telegram?: unknown; email?: unknown }>('POST /api/admin/alerts/test', { body: {} });
  } catch (e) {
    if (!alive()) return;
    setAlertTest(ctx, { busy: false });
    ctx.gp.ui.toast(ctx.gp.ui.err(e));
    return;
  }
  if (!alive()) return;
  const result = { telegram: parseChannel(r.telegram), email: parseChannel(r.email) };
  setAlertTest(ctx, { busy: false, result });
  const t = ctx.t;
  const word = (c: AlertTestChannel): string => str(t, c.ok ? 'cfgAlertTestOkShort' : 'cfgAlertTestFailShort');
  ctx.gp.ui.toast(interpolate(str(t, 'cfgAlertTestDone'), { tg: word(result.telegram), email: word(result.email) }));
}

function alertTestVals(ctx: AdminCtx, g: Gate): Record<string, unknown> {
  const t = ctx.t;
  const at = ctx.s.st.alertTest;
  const row = (label: string, c: AlertTestChannel): { text: string; style: string } => ({
    text: `${str(t, label)}：${c.ok ? str(t, 'cfgAlertTestOk') : interpolate(str(t, 'cfgAlertTestFail'), { err: c.error || str(t, 'cfgAlertTestUnknown') })}`,
    style: c.ok ? ALERT_TEST_OK : ALERT_TEST_FAIL,
  });
  return {
    testShow: true,
    testLabel: str(t, 'cfgAlertTest'),
    testHint: str(t, 'cfgAlertTestHint'),
    testTitle: g.title,
    testBusy: at.busy,
    testStyle: g.disabled || at.busy ? gatedStyle(ALERT_TEST_BTN, { disabled: true, title: g.title }) : ALERT_TEST_BTN,
    testSpinStyle: ALERT_TEST_SPIN,
    testSend: () => {
      if (!g.disabled) void sendAlertTest(ctx);
    },
    testRows: at.result ? [row('cfgAlertTestTg', at.result.telegram), row('cfgAlertTestEmail', at.result.email)] : [],
  };
}

const NO_ALERT_TEST: Readonly<Record<string, unknown>> = { testShow: false, testRows: [] };

function leaveDiscard(ctx: AdminCtx): void {
  const st = ctx.host.state.st;
  const to = st.leaveTo;
  if (to === null) return;
  setSt(ctx, { items: st.base, dirty: false, invalid: [], err: '', leaveTo: null });
  ctx.host.setState({ page: to, tab: 0 });
  void ctx.gp.session.refreshMe();
}

// 浏览器刷新 / 关闭标签页时的拦截（浏览器原生离开提示，只能由 beforeunload 触发）
function beforeUnload(ctx: AdminCtx): (e: BeforeUnloadEvent) => void {
  return (e) => {
    const s = ctx.host.state;
    if (s.page !== 'settings' || !s.st.dirty) return;
    e.preventDefault();
    e.returnValue = '';
  };
}

function groupInt(v: string): string {
  const n = parseDecimalInput(v, 6);
  if (n === null) return v;
  const [i = '0', f] = n.split('.');
  return `${i.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${f ? `.${f}` : ''}`;
}

function settingsVals(ctx: AdminCtx): Record<string, unknown> {
  const { t, lang } = ctx;
  const st = ctx.s.st;
  const C = st.items;
  const val = (k: string): string => C[k] ?? '';
  const editable = canEdit(ctx);
  const g = gate(ctx.s.sh.me, t, 'settings', 'edit');
  const unit = lang === 'zh' ? '笔' : 'tx'; // 原型 L969
  const field = (d: SettingDef): Record<string, unknown> => {
    const value = val(d.key);
    const on = value === '1';
    const invalid = st.invalid.includes(d.key);
    const changed = d.kind !== 'ro' && st.loaded && (st.items[d.key] ?? '') !== (st.base[d.key] ?? '');
    const base = d.kind === 'text' ? textStyle(!!d.wide) : NUM_STYLE;
    // D-42：仅在本页已加载后判空（未加载时 items 为空，不能把全部字段当成「未配置」误报）
    const emptyWarn = st.loaded && EMPTY_WARN_KEYS.has(d.key) && value.trim() === '';
    return {
      k: d.key,
      label: str(t, d.label),
      warn: emptyWarn,
      warnText: emptyWarn ? str(t, 'cfgEmptyWarn') : '',
      warnStyle: WARN_STYLE,
      suffix: d.suffix === 'unitLevel' ? str(t, 'unitLevel') : d.suffix ?? '',
      isNum: d.kind === 'num',
      isSwitch: d.kind === 'switch',
      isText: d.kind === 'text',
      isRo: d.kind === 'ro',
      value,
      on,
      invalid,
      changed,
      inputStyle: invalid ? `${base}${INVALID}` : changed ? `${base}${CHANGED}` : base,
      onChange: (e: InputEvent) => setItem(ctx, d.key, e.target.value),
      toggle: () => setItem(ctx, d.key, on ? '0' : '1'),
      // 开关切换只改本页草稿：未保存时文字明示「（未保存）」，避免误以为已生效
      text: changed ? interpolate(str(t, 'cfgUsSwitchUnsaved'), { state: str(t, on ? 'stOn' : 'stOff') }) : str(t, on ? 'stOn' : 'stOff'),
      textStyle: `font-size:11px;font-weight:700;color:${changed ? '#c47a0c' : '#8a958f'}`,
      // 原型 L884–885 开关样式串逐字
      style: `width:52px;height:30px;border-radius:999px;border:0;cursor:${editable ? 'pointer' : 'not-allowed'};position:relative;background:${on ? '#0d9a68' : '#cfd6d2'}${changed ? SWITCH_CHANGED : ''}`,
      knob: `position:absolute;top:3px;left:${on ? '25px' : '3px'};width:24px;height:24px;border-radius:50%;background:#fff;transition:left .18s`,
    };
  };
  const settleInvalid = st.invalid.includes('settleTime');
  const dirtyKeys = st.loaded ? changedKeys(st.items, st.base) : [];
  return {
    cfgGroups: GROUPS.map((gr) => {
      const hk = GROUP_HINTS[gr.title];
      const test = gr.title === ALERT_TEST_GROUP ? alertTestVals(ctx, g) : NO_ALERT_TEST;
      return { key: gr.title, title: str(t, gr.title), hint: hk ? hk.map((k) => str(t, k)).join(' ') : '', hintStyle: GROUP_HINT_STYLE, fields: gr.fields.map(field), ...test };
    }),
    cfgDirty: st.dirty,
    cfgSaving: st.saving,
    cfgSettleTime: val('settleTime'),
    onSettleTime: (e: InputEvent) => setItem(ctx, 'settleTime', e.target.value),
    cfgSettleStyle: settleInvalid ? `${textStyle(false)}${INVALID}` : dirtyKeys.includes('settleTime') ? `${textStyle(false)}${CHANGED}` : textStyle(false),
    cfgMaintText: val('maintText'),
    onMaintText: (e: InputEvent) => setItem(ctx, 'maintText', e.target.value),
    saveConfig: () => {
      if (!g.disabled) void saveSettings(ctx);
    },
    cfgSaveStyle: g.disabled || dirtyKeys.length > 0 ? gatedStyle(SAVE_STYLE, g) : `${SAVE_STYLE}${SAVE_IDLE}`,
    cfgDirtyBar: dirtyKeys.length ? interpolate(str(t, 'cfgUsDirtyBar'), { n: dirtyKeys.length, labels: labelList(ctx, dirtyKeys) }) : '',
    cfgDirtyBarStyle: DIRTY_BAR_STYLE,
    cfgLeaveOpen: st.leaveTo !== null,
    cfgLeaveText: interpolate(str(t, 'cfgUsLeaveText'), { n: dirtyKeys.length, labels: labelList(ctx, dirtyKeys) }),
    cfgLeaveTitle: str(t, 'cfgUsLeaveTitle'),
    cfgLeaveStay: str(t, 'cfgUsLeaveStay'),
    cfgLeaveGo: str(t, 'cfgUsLeaveGo'),
    leaveStay: () => setSt(ctx, { leaveTo: null }),
    leaveGo: () => leaveDiscard(ctx),
    cfgSaveTitle: g.title,
    cfgReadonly: !editable,
    cfgErr: st.err,
    cfgWarn: st.warn,
    // 用户 2026-10-18：预览只列用户端实际生效的平台级费用；卡类费用以「卡产品与风控」各产品配置为准，不在此按默认值展示
    cfgPreviewRows: [
      [str(t, 'cfgDepositFeeRate'), `${val('depositFeeRate')}%`],
      [str(t, 'cfgMinDeposit'), `${val('minDeposit')} USDT`],
      [str(t, 'cfgMinWithdraw'), `${val('minWithdraw')} USDT`],
      [str(t, 'cfgPrevWithdrawFee'), `${val('withdrawFeeRate')}% + ${val('feeTrc')} USDT/${unit}`],
      [str(t, 'cfgCardWithdrawFee'), `${val('cardWithdrawFee')} USDT/${unit}`],
      [`${str(t, 'cfgL1Open')} / ${str(t, 'cfgL1Top')}`, `$${val('l1Open')} / ${val('l1Top')}%`],
      [str(t, 'cfgKycLimit'), `$${groupInt(val('kycLimit'))}`],
      [str(t, 'cfgPrevCardFees'), str(t, 'cfgPrevCardFeesVal')],
      [str(t, 'cfgPrevSettle'), interpolate(str(t, 'cfgPrevSettleVal'), { time: val('settleTime'), tz: val('timezone') })],
    ].map(([k, v]) => ({ k, v })),
  };
}

export const settings: AdminDomain = {
  name: 'settings',
  keys: KEYS,
  i18n: I18N_SETTINGS,
  rewrites: REWRITES_SETTINGS,
  legacyKeysFor: () => [],
  mount(ctx) {
    unsubs.get(ctx.host)?.forEach((off) => off());
    runs.set(ctx.host, { ready: false });
    const offUnload: Array<() => void> = [];
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      const h = beforeUnload(ctx);
      window.addEventListener('beforeunload', h);
      offUnload.push(() => window.removeEventListener('beforeunload', h));
    }
    unsubs.set(ctx.host, [
      ...offUnload,
      ctx.events.on('session:lost', () => resetSession(ctx, false)),
      ctx.events.on('session:ready', () => {
        resetSession(ctx, true);
        if (ctx.host.state.page === 'settings') void loadSettings(ctx);
      }),
      // 加载失败后的重试入口：侧栏同页重复点击（外壳发 screen{from===to}，E-10）且尚未加载成功时重拉；已加载时不覆盖未保存的编辑
      ctx.events.on('screen', (p) => {
        if (p.from !== p.to || p.to !== 'settings' || !runOf(ctx).ready || ctx.host.state.st.loaded) return;
        void loadSettings(ctx);
      }),
    ]);
  },
  unmount(ctx) {
    unsubs.get(ctx.host)?.forEach((off) => off());
    unsubs.delete(ctx.host);
    runs.delete(ctx.host);
  },
  update(ctx, prev) {
    const s = ctx.host.state;
    if (prev.page === s.page || s.page !== 'settings' || !runOf(ctx).ready) return;
    void loadSettings(ctx);
  },
  vals: (ctx) => settingsVals(ctx),
};
